/**
 * Step 2 — Asset Identification.
 *
 * Story 4.C built the read-mode dispatcher (spinner / table / hero).
 * Story 4.D wires CRUD: Add button + per-row Edit/Delete via
 * `useRecordCRUDv2`. Add/Edit open an `AssetFormModal`; Delete opens
 * the useConfirm dialog. On success we refetch the asset list.
 *
 * Story 4.E adds the cascade-delete path: when the backend returns
 * `HAS_DEPENDENCIES` from a plain delete, swap in CascadeDeleteDialog
 * which offers Cancel / Delete Only (`force: true`) /
 * Delete All Related (`cascade: true`).
 */
import React, { useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import CascadeDeleteDialog from '../components/CascadeDeleteDialog';
import AssetTable from './step2-assets/AssetTable';
import AssetFormModal from './step2-assets/AssetFormModal';
import HierarchicalAssetReview from './step2-assets/HierarchicalAssetReview';
import type { ChildAssetView } from './step2-assets/ChildAssetsTable';
import type { Step2BodyProps } from './stepBodyProps';
import type { AssetRecord } from './step2-assets/assetFieldSchema';
import { exportAssetsToCsv, parseAssetFile } from './step2-assets/assetCsv';
import { notify } from '../../../services/notificationBus';
import styles from './Step2AssetIdentification.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

const CascadeDeleteDialogAny = CascadeDeleteDialog as unknown as React.ComponentType<any>;

interface DependentRecord {
  id?: string;
  type?: string;
  description?: string;
  [k: string]: unknown;
}

interface CascadeState {
  asset: AssetRecord;
  dependents: DependentRecord[];
}

type ModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; asset: AssetRecord };

export default function Step2AssetIdentification({
  status,
  editable,
  structured,
  crud,
  hierarchical,
  errorMessage,
}: Step2BodyProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const [cascade, setCascade] = useState<CascadeState | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Asset identification failed"
        description={errorMessage ?? structured.error ?? 'The asset agent encountered an error. Revise and retry.'}
      />
    );
  }

  const assets = (structured.data ?? []) as AssetRecord[];

  // Spinner conditions:
  //   - Status-driven: agent isn't done yet (empty / generating / revising)
  //   - Initial load: no data yet AND fetch is in flight
  // Once data is in hand, post-save refetches stay invisible — the
  // table keeps the previous rows and re-renders with the new data
  // on success. Otherwise every Save would flash the polling spinner.
  const noDataYet = assets.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Identifying assets"
        description="The asset agent is mining the item definition for critical assets. This usually takes 1–2 minutes."
      />
    );
  }

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      if (modal?.kind === 'edit') {
        await crud.update(modal.asset.asset_id, values);
        notify({ type: 'success', message: `Asset "${values.asset_name}" updated.` });
      } else {
        await crud.add(values);
        notify({ type: 'success', message: `Asset "${values.asset_name}" added.` });
      }
      setModal(null);
      structured.refetch();
    } catch (e: any) {
      // Re-throw so FormModal surfaces the inline error on the form.
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save asset';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save asset');
    }
  };

  const handleDelete = async (target: AssetRecord) => {
    const ok = await confirm({
      title: 'Delete asset?',
      description: `Delete asset "${target.asset_name}" (${target.asset_id})? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.asset_id);
      notify({ type: 'success', message: `Asset "${target.asset_name}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      // Backend returns 409 with `code === 'HAS_DEPENDENCIES'` when
      // the asset is referenced by downstream records (threats,
      // scenarios, attack paths, risks, requirements). Swap the
      // plain confirm for the cascade dialog so the user can choose
      // between dropping the asset alone (force) or dropping the
      // whole dependency chain (cascade).
      if (e?.code === 'HAS_DEPENDENCIES' && Array.isArray(e?.dependentRecords)) {
        setCascade({ asset: target, dependents: e.dependentRecords as DependentRecord[] });
        return;
      }
      const msg = e?.message || 'Failed to delete asset';
      notify({ type: 'error', title: 'Delete failed', message: msg });
    }
  };

  const handleExport = () => {
    exportAssetsToCsv(assets);
  };

  const handleImport = async (file: File) => {
    let records: AssetRecord[];
    try {
      records = await parseAssetFile(file);
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Import failed',
        message: e?.message || `Could not parse ${file.name}`,
      });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no asset rows.` });
      return;
    }

    const existingIds = new Set(assets.map((a) => a.asset_id));
    let added = 0;
    let skipped = 0;
    let failed = 0;
    for (const record of records) {
      if (!record?.asset_id) {
        failed += 1;
        continue;
      }
      if (existingIds.has(record.asset_id)) {
        skipped += 1;
        continue;
      }
      try {
        await crud.add(record as Record<string, unknown>);
        existingIds.add(record.asset_id);
        added += 1;
      } catch {
        failed += 1;
      }
    }

    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  const handleCascadeAction = async (mode: 'force' | 'cascade') => {
    if (!cascade) return;
    const target = cascade.asset;
    try {
      const result: any = await crud.remove(target.asset_id, { [mode]: true });
      if (mode === 'cascade') {
        const count = Array.isArray(result?.cascade_results) ? result.cascade_results.length : 0;
        notify({
          type: 'info',
          message: `Deleted "${target.asset_name}" and ${count} dependent record${count !== 1 ? 's' : ''}.`,
        });
      } else {
        notify({
          type: 'warning',
          title: 'Deleted with broken references',
          message: `"${target.asset_name}" deleted; dependent records may now reference a missing asset.`,
        });
      }
      setCascade(null);
      structured.refetch();
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Delete failed',
        message: e?.message || 'Failed to delete asset',
      });
      setCascade(null);
    }
  };

  // Split assets for hierarchical projects. The hook only sets
  // `hierarchical.enabled` when at least one child-TARA-sourced
  // asset is present, so this filter is safe to evaluate here.
  const newAssets = hierarchical.enabled
    ? assets.filter((a: any) => a?.source !== 'child_tara')
    : assets;
  const childAssets: ChildAssetView[] = hierarchical.enabled
    ? assets.filter((a: any) => a?.source === 'child_tara')
    : [];

  return (
    <div className={styles.bleed}>
      {hierarchical.enabled ? (
        <HierarchicalAssetReview
          newAssets={newAssets}
          childAssets={childAssets}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(asset) => setModal({ kind: 'edit', asset })}
          onDelete={(asset) => handleDelete(asset)}
          onExport={handleExport}
          onImport={handleImport}
          modifications={hierarchical.modifications}
          onToggleRelevance={hierarchical.setRelevance}
          onJustificationChange={hierarchical.setJustification}
          childInputsDisabled={hierarchical.approving || !editable}
          onSaveChildModifications={hierarchical.onApprove}
        />
      ) : (
        <AssetTable
          assets={assets}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(asset) => setModal({ kind: 'edit', asset })}
          onDelete={(asset) => handleDelete(asset)}
          onExport={handleExport}
          onImport={handleImport}
        />
      )}
      {modal && (
        <AssetFormModal
          mode={modal.kind}
          initialAsset={modal.kind === 'edit' ? modal.asset : null}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
      <CascadeDeleteDialogAny
        isOpen={!!cascade}
        recordType="Asset"
        recordId={cascade?.asset.asset_id ?? ''}
        dependentRecords={cascade?.dependents ?? []}
        isDeleting={crud.saving}
        onClose={() => setCascade(null)}
        onDeleteOnly={() => handleCascadeAction('force')}
        onCascadeDelete={() => handleCascadeAction('cascade')}
      />
    </div>
  );
}
